// Games-side persistence. Reuses the shared Firebase connection (same `db` the
// G-New Features PIN gate already uses) but reads/writes ONLY under ggGames/*
// via gpath(). It never references tournaments/*, scoring.js, or any tourney
// RTDB node — the games side is a separate world of state.
//
// Schema:  ggGames/{id} = {
//   meta:   { gameId, name, createdAt, status:'active'|'final', course:{...}, options:{...}, handicapsOn, allowance },
//   players:[ { id, name, handicapIndex, courseHandicap, playingHandicap } ],
//   scores: { [hole]: { [playerId]: gross } },
//   settlement?: {...}   // snapshot written on finalize
// }
import { ref, push, set, update, onValue, get, remove } from 'firebase/database'
// GGC game data lives on the shared completetourneylive backend (same as the
// companion app) — NOT the build's default db. See src/lib/ggBackend.js.
import { ggDb as db } from '../../lib/ggBackend.js'
import { gpath } from './ggGamesPaths.js'
import { stripUndefinedDeep } from './ggGamesSanitize.js'

export async function createGame(meta, players) {
  const node = push(ref(db, gpath()))
  // RTDB set() throws on ANY undefined anywhere in the payload — strip first.
  const payload = stripUndefinedDeep({
    meta: { ...meta, createdAt: Date.now(), status: 'active' },
    players: players || [],
    scores: {},
  })
  await set(node, payload)
  return node.key
}

export async function setScore(id, hole, playerId, gross) {
  const path = gpath(id, 'scores', hole, playerId)
  const n = Number(gross)
  if (gross == null || gross === '' || !Number.isFinite(n) || n <= 0) {
    await remove(ref(db, path))
    return
  }
  await set(ref(db, path), n)
}

// Game-specific per-hole state (e.g. Wolf picks) under ggGames/{id}/{key}/{hole}.
export async function setGameHole(id, key, hole, value) {
  await set(ref(db, gpath(id, key, hole)), value)
}

// Set/clear a value at an arbitrary sub-path (e.g. Wolf Press/Double).
export async function setGameField(id, subpath, value) {
  const p = gpath(id, subpath)
  if (value == null) await remove(ref(db, p))
  else await set(ref(db, p), value)
}

// ── Saved rosters (reusable, editable next time) under ggGames/_rosters/{id} ──
export async function saveRoster(name, rows) {
  const node = push(ref(db, gpath('_rosters')))
  await set(node, { name: name || 'Roster', rows: rows || [], savedAt: Date.now() })
  return node.key
}
export function watchRosters(cb) {
  return onValue(ref(db, gpath('_rosters')), (s) => {
    const v = s.val() || {}
    cb(Object.entries(v).map(([id, r]) => ({ id, ...r })).sort((a, b) => (b.savedAt || 0) - (a.savedAt || 0)))
  })
}
export async function deleteRoster(id) {
  await remove(ref(db, gpath('_rosters', id)))
}

// ── Saved / recent courses (reference data) under ggGames/_courses/{apiId} ────
export async function saveCourse(course) {
  if (!course?.id) return
  await set(ref(db, gpath('_courses', String(course.id))), { ...course, savedAt: Date.now() })
}
export function watchCourses(cb) {
  return onValue(ref(db, gpath('_courses')), (s) => {
    const v = s.val() || {}
    cb(Object.values(v).sort((a, b) => (b.savedAt || 0) - (a.savedAt || 0)))
  })
}

export async function saveSettlement(id, settlement) {
  await update(ref(db, gpath(id)), { settlement, 'meta/status': 'final' })
}

export async function reopenGame(id) {
  await update(ref(db, gpath(id, 'meta')), { status: 'active' })
}

export async function deleteGame(id) {
  await remove(ref(db, gpath(id)))
}

export function watchGame(id, cb) {
  return onValue(ref(db, gpath(id)), (s) => cb(s.val() || null))
}

export function watchGames(cb) {
  return onValue(ref(db, gpath()), (s) => {
    const v = s.val() || {}
    cb(Object.entries(v)
      .filter(([id]) => id !== '_rosters' && id !== '_courses') // not games
      .map(([id, g]) => ({ id, ...g }))
      .sort((a, b) => (b.meta?.createdAt || 0) - (a.meta?.createdAt || 0)))
  })
}

export async function loadGame(id) {
  const s = await get(ref(db, gpath(id)))
  return s.val() || null
}
